import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CreditCard, Building2, CheckCircle, Loader2, Globe } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';
import { COUNTRY_NAMES, resolveFeeState } from '@/lib/foreignCatalog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const InternationalOrderPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();

  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(null);
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [govtFee, setGovtFee] = useState(0);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeUnavailable, setFeeUnavailable] = useState(false);

  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const wanted = searchParams.get('country');
        let foreign = (res.data.companies || []).filter(c => c.country && c.country !== 'US');
        if (wanted) foreign = foreign.filter(c => c.country === wanted);
        setCompanies(foreign);
        const pre = searchParams.get('company');
        if (pre) {
          const c = foreign.find(x => x.id === pre);
          if (c) selectCompany(c);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const selectCompany = async (c) => {
    setCompany(c);
    setProduct(null);
    setGovtFee(0);
    try {
      const res = await axios.get(`${API}/products`, { params: { country: c.country, limit: 100 }, headers: { Authorization: `Bearer ${token}` } });
      setProducts((res.data.products || []).filter(p => p.pricing_logic));
    } catch {
      setProducts([]);
    }
  };

  const selectProduct = async (p) => {
    setProduct(p);
    setGovtFee(0);
    setFeeUnavailable(false);
    if (!p.has_state_fee) return;
    const feeState = resolveFeeState(p, company);
    if (!feeState) { setFeeUnavailable(true); return; }
    setFeeLoading(true);
    try {
      const res = await axios.get(`${API}/state-fees/lookup`, { params: { service_type: p.service_type, state: feeState }, headers: { Authorization: `Bearer ${token}` } });
      const fees = res.data.state_fees || [];
      const gf = fees.length ? (fees[0].govt_fee || 0) : 0;
      setGovtFee(gf);
      setFeeUnavailable(fees.length === 0);
    } catch {
      setFeeUnavailable(true);
    } finally {
      setFeeLoading(false);
    }
  };

  const platformFee = product ? (product.platform_fees || 0) : 0;
  const total = platformFee + (govtFee || 0);

  const createOrder = async () => {
    const feeState = resolveFeeState(product, company);
    const res = await axios.post(`${API}/orders`, {
      company_id: company.id,
      product_id: product.id,
      service_type: product.service_type,
      fee_state: feeState,
    }, { headers: { Authorization: `Bearer ${token}` } });
    return res.data.id;
  };

  const payCard = async () => {
    if (loading) return;
    setLoading(true); setError('');
    try {
      const oid = await createOrder();
      const pay = await axios.post(`${API}/payments/stripe/checkout`, { order_ids: [oid], origin_url: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
      if (pay.data.checkout_url) window.location.href = pay.data.checkout_url;
      else { setError('Failed to start payment. Try bank transfer.'); setLoading(false); }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  const payBank = async () => {
    if (loading) return;
    setLoading(true); setError('');
    try {
      const oid = await createOrder();
      setOrderId(oid); setOrderSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create order.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('success') === 'true' && searchParams.get('order_id')) {
      setOrderId(searchParams.get('order_id')); setOrderSuccess(true);
    }
  }, [searchParams]);

  // Preselect a product (from the "Create Order" renewal links) and jump to payment.
  useEffect(() => {
    const pid = searchParams.get('product');
    if (pid && products.length && !product) {
      const p = products.find(x => x.service_type === pid);
      if (p) { selectProduct(p); setStep(2); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  if (orderSuccess) {
    return (
      <AppLayout>
        <div className="max-w-lg mx-auto text-center py-12">
          <div className="w-20 h-20 bg-[#059669] rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-gray-900" /></div>
          <h1 className="text-2xl font-bold text-white mb-4">Order Placed</h1>
          <p className="text-gray-400 mb-2">Your international filing order has been submitted and is being processed.</p>
          {orderId && <p className="text-gray-500 text-sm mb-8">Order ID: <span className="text-white font-mono">{orderId}</span></p>}
          <Link to="/dashboard/orders" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white" style={{ backgroundColor: '#059669' }} data-testid="view-orders-btn">View My Orders</Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Link to="/dashboard/orders" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"><ArrowLeft className="w-4 h-4" /> Back to Orders</Link>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Globe className="w-6 h-6 text-[#059669]" /> New Filing Order</h1>
        <p className="text-gray-400 text-sm mt-1">Order compliance filings for your UK, Canada, Singapore or UAE company.</p>
      </div>

      {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6"><p className="text-red-400 text-sm">{error}</p></div>}

      {companies.length === 0 ? (
        <div className="bg-[#111827] border border-gray-800 rounded-lg p-8 text-center max-w-lg">
          <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-white font-medium mb-2">No Companies Yet</h3>
          <p className="text-gray-400 text-sm mb-4">Set up an international company to place filing orders.</p>
          <Link to="/dashboard/onboarding/international" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: '#059669' }} data-testid="go-onboarding-btn">Set up a Company</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Company */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-3">Select Company</h2>
              <select value={company?.id || ''} onChange={e => { const c = companies.find(x => x.id === e.target.value); if (c) selectCompany(c); }} className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#059669]" data-testid="intl-company-select">
                <option value="">Select a company</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.company_name} — {COUNTRY_NAMES[c.country] || c.country} ({c.state_of_formation})</option>)}
              </select>
            </div>

            {/* Products */}
            {company && (
              <div>
                <h2 className="text-lg font-semibold text-white mb-3">Select a Filing / Service</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="intl-products">
                  {products.map(p => (
                    <button key={p.id} onClick={() => selectProduct(p)} className={`p-4 rounded-lg border-2 text-left transition-all ${product?.id === p.id ? 'border-[#059669] bg-[#059669]/10' : 'border-gray-700 hover:border-gray-600'}`} data-testid={`intl-product-${p.service_type}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-white text-sm font-medium">{p.product_name}</p>
                          <p className="text-gray-500 text-xs mt-0.5">{p.category}</p>
                        </div>
                        <span className="text-[#059669] text-sm font-semibold whitespace-nowrap">{money(p.platform_fees)}{p.has_state_fee ? ' + govt' : ''}</span>
                      </div>
                    </button>
                  ))}
                  {products.length === 0 && <p className="text-gray-500 text-sm">No products available for this country.</p>}
                </div>
              </div>
            )}

            {product && step === 1 && (
              <div className="flex justify-end">
                <button onClick={() => setStep(2)} className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white" style={{ backgroundColor: '#059669' }} data-testid="intl-proceed-btn">
                  Proceed to Payment <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Payment */}
            {step === 2 && product && (
              <div className="border-t border-gray-800 pt-6">
                <h2 className="text-lg font-semibold text-white mb-4">Payment Method</h2>
                <div className="space-y-3">
                  <button onClick={() => setPaymentMethod('card')} className={`w-full text-left p-5 rounded-lg border-2 transition-all ${paymentMethod === 'card' ? 'border-[#059669] bg-[#059669]/10' : 'border-gray-800 bg-[#111827] hover:border-gray-600'}`} data-testid="intl-pay-card-option">
                    <div className="flex items-center gap-4"><CreditCard className="w-6 h-6 text-[#059669]" /><div><h3 className="text-white font-semibold">Pay with Card</h3><p className="text-gray-400 text-sm">Secure payment via Stripe</p></div></div>
                  </button>
                  <button onClick={() => setPaymentMethod('bank')} className={`w-full text-left p-5 rounded-lg border-2 transition-all ${paymentMethod === 'bank' ? 'border-[#059669] bg-[#059669]/10' : 'border-gray-800 bg-[#111827] hover:border-gray-600'}`} data-testid="intl-pay-bank-option">
                    <div className="flex items-center gap-4"><Building2 className="w-6 h-6 text-[#059669]" /><div><h3 className="text-white font-semibold">Pay by Bank Transfer</h3><p className="text-gray-400 text-sm">Manual transfer — verified by our team</p></div></div>
                  </button>
                </div>
                <div className="mt-6">
                  {paymentMethod === 'card' ? (
                    <button onClick={payCard} disabled={loading} className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#059669' }} data-testid="intl-pay-card-btn">
                      {loading ? 'Processing...' : `Pay ${money(total)}`} <CreditCard className="w-5 h-5" />
                    </button>
                  ) : (
                    <button onClick={payBank} disabled={loading} className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#059669' }} data-testid="intl-pay-bank-btn">
                      {loading ? 'Processing...' : 'Place Order'} <ArrowRight className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <div>
            {product && (
              <div className="bg-[#111827] border border-gray-800 rounded-lg p-6 sticky top-6" data-testid="intl-order-summary">
                <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-gray-400">Platform Fee</span><span className="text-white">{money(platformFee)}</span></div>
                  {product.has_state_fee && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Government Fee</span>
                      <span className="text-white">
                        {feeLoading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : feeUnavailable ? <span className="text-gray-500">TBD</span> : money(govtFee)}
                      </span>
                    </div>
                  )}
                  <div className="text-gray-500 text-xs">{product.product_name}</div>
                  <div className="border-t border-gray-700 pt-3 flex justify-between">
                    <span className="text-white font-semibold">Total</span>
                    <span className="text-[#059669] font-bold" data-testid="intl-total">{money(total)}</span>
                  </div>
                  {product.has_state_fee && feeUnavailable && <p className="text-gray-500 text-xs">Government fee will be confirmed by our team before processing.</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default InternationalOrderPage;
